<template>
  <Head title="Administración de Multimedia" />

  <Layout :auth="auth" :change="change" :appVersion="appVersion">
    <div class="max-w-4xl mx-auto">
      <div class="bg-white rounded-lg shadow-md p-6">
        <h1 class="text-2xl font-bold text-gray-900 mb-6">
          Administración de Multimedia
        </h1>

        <!-- Estadísticas de Multimedia -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div class="bg-blue-50 p-4 rounded-lg">
            <div class="flex items-center">
              <div class="p-2 bg-blue-100 rounded-lg">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-blue-600">Imágenes de Cuentas</p>
                <p class="text-2xl font-bold text-blue-900">{{ stats.accounts || 0 }}</p>
              </div>
            </div>
          </div>

          <div class="bg-green-50 p-4 rounded-lg">
            <div class="flex items-center">
              <div class="p-2 bg-green-100 rounded-lg">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-green-600">Imágenes de Productos</p>
                <p class="text-2xl font-bold text-green-900">{{ stats.products || 0 }}</p>
              </div>
            </div>
          </div>

          <div class="bg-purple-50 p-4 rounded-lg">
            <div class="flex items-center">
              <div class="p-2 bg-purple-100 rounded-lg">
                <svg class="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <div class="ml-4">
                <p class="text-sm font-medium text-purple-600">Archivos Públicos</p>
                <p class="text-2xl font-bold text-purple-900">{{ stats.public || 0 }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Funciones de Exportar e Importar -->
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <!-- Exportar Multimedia -->
          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center mb-4">
              <div class="p-2 bg-blue-100 rounded-lg">
                <svg class="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
              </div>
              <h3 class="ml-3 text-lg font-medium text-gray-900">Exportar Multimedia</h3>
            </div>
            
            <p class="text-gray-600 mb-4">
              Descarga todas las imágenes y archivos multimedia del sistema en un archivo ZIP.
            </p>

            <div class="space-y-3">
              <div class="flex items-center text-sm text-gray-600">
                <svg class="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Incluye imágenes de cuentas
              </div>
              <div class="flex items-center text-sm text-gray-600">
                <svg class="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Incluye imágenes de productos
              </div>
              <div class="flex items-center text-sm text-gray-600">
                <svg class="w-4 h-4 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
                Incluye archivos públicos
              </div>
            </div>

            <button
              @click="exportMultimedia"
              :disabled="exporting"
              class="mt-4 w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              <svg v-if="exporting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ exporting ? 'Exportando...' : 'Exportar Multimedia' }}
            </button>
          </div>

          <!-- Importar Multimedia -->
          <div class="bg-white border border-gray-200 rounded-lg p-6">
            <div class="flex items-center mb-4">
              <div class="p-2 bg-green-100 rounded-lg">
                <svg class="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"></path>
                </svg>
              </div>
              <h3 class="ml-3 text-lg font-medium text-gray-900">Importar Multimedia</h3>
            </div>
            
            <p class="text-gray-600 mb-4">
              Sube un archivo ZIP con multimedia para restaurar o agregar archivos al sistema.
            </p>

            <div class="space-y-3 mb-4">
              <div class="flex items-center text-sm text-gray-600">
                <svg class="w-4 h-4 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                Máximo 100MB
              </div>
              <div class="flex items-center text-sm text-gray-600">
                <svg class="w-4 h-4 mr-2 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
                </svg>
                Solo archivos ZIP
              </div>
            </div>

            <form @submit.prevent="importMultimedia" class="space-y-4">
              <div class="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                <input
                  ref="fileInput"
                  type="file"
                  accept=".zip"
                  @change="handleFileSelect"
                  class="hidden"
                />
                <div v-if="!selectedFile" @click="$refs.fileInput.click()" class="cursor-pointer">
                  <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                    <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                  </svg>
                  <p class="mt-1 text-sm text-gray-600">
                    Haz clic para seleccionar un archivo ZIP
                  </p>
                </div>
                <div v-else class="text-left">
                  <div class="flex items-center justify-between">
                    <div class="flex items-center">
                      <svg class="w-8 h-8 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <div>
                        <p class="text-sm font-medium text-gray-900">{{ selectedFile.name }}</p>
                        <p class="text-sm text-gray-500">{{ formatFileSize(selectedFile.size) }}</p>
                      </div>
                    </div>
                    <button
                      type="button"
                      @click="removeFile"
                      class="text-red-500 hover:text-red-700"
                    >
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                :disabled="!selectedFile || importing"
                class="w-full bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                <svg v-if="importing" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ importing ? 'Importando...' : 'Importar Multimedia' }}
              </button>
            </form>
          </div>
        </div>

        <!-- Información Adicional -->
        <div class="mt-8 bg-gray-50 rounded-lg p-6">
          <h3 class="text-lg font-medium text-gray-900 mb-4">Información Importante</h3>
          <div class="space-y-3 text-sm text-gray-600">
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p>La exportación incluye todas las imágenes de cuentas, productos y archivos públicos del sistema.</p>
            </div>
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p>La importación reemplazará archivos existentes con el mismo nombre. Se recomienda hacer una copia de seguridad antes de importar.</p>
            </div>
            <div class="flex items-start">
              <svg class="w-5 h-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
              <p>Los archivos se organizan automáticamente en las carpetas correspondientes según la estructura del ZIP.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  components: {
    Head,
    Link,
    Layout
  },
  props: {
    auth: Object,
    change: String,
    appVersion: String,
    stats: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      selectedFile: null,
      exporting: false,
      importing: false
    }
  },
  methods: {
    async exportMultimedia() {
      try {
        this.exporting = true;
        
        // Verificar si el usuario está autenticado
        if (!this.auth.user) {
          this.$toast.error('Debes iniciar sesión para exportar multimedia');
          return;
        }
        
        // Primero verificar si la exportación es posible
        const checkResponse = await fetch(route('multimedia.check'), {
          method: 'GET',
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
            'Accept': 'application/json'
          }
        });
        
        const checkData = await checkResponse.json();
        
        if (!checkData.success || !checkData.canExport) {
          this.$toast.error(checkData.message || 'No se puede exportar multimedia en este momento');
          if (checkData.errors && checkData.errors.length > 0) {
            console.error('Errores de exportación:', checkData.errors);
          }
          return;
        }
        
        // Si la verificación es exitosa, proceder con la descarga
        const link = document.createElement('a');
        link.href = route('multimedia.export');
        link.download = `realgestion_multimedia_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.zip`;
        
        // Agregar el enlace al DOM y hacer clic
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Esperar un momento para que la descarga comience
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Mostrar mensaje de éxito
        this.$toast.success(`Descarga iniciada. Se exportarán ${checkData.totalFiles} archivos.`);
        
      } catch (error) {
        console.error('Error al exportar multimedia:', error);
        
        // Mostrar información detallada del error
        let errorMessage = 'Error al exportar multimedia';
        
        if (error.response) {
          // Error de respuesta del servidor
          if (error.response.status === 401) {
            errorMessage = 'No tienes permisos para exportar multimedia. Por favor, inicia sesión nuevamente.';
          } else if (error.response.status === 403) {
            errorMessage = 'Acceso denegado. No tienes permisos para esta acción.';
          } else if (error.response.status === 404) {
            errorMessage = 'La ruta de exportación no se encontró. Contacta al administrador.';
          } else if (error.response.status === 500) {
            errorMessage = 'Error interno del servidor. Contacta al administrador.';
          } else {
            errorMessage = `Error del servidor (${error.response.status}): ${error.response.data?.message || 'Error desconocido'}`;
          }
        } else if (error.request) {
          // Error de red
          errorMessage = 'Error de conexión. Verifica tu conexión a internet.';
        } else {
          // Otro tipo de error
          errorMessage = `Error: ${error.message}`;
        }
        
        this.$toast.error(errorMessage);
      } finally {
        this.exporting = false;
      }
    },

    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file && file.type === 'application/zip') {
        this.selectedFile = file
      } else {
        this.$toast.error('Por favor selecciona un archivo ZIP válido')
        this.removeFile()
      }
    },

    removeFile() {
      this.selectedFile = null
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = ''
      }
    },

    async importMultimedia() {
      if (!this.selectedFile) {
        this.$toast.error('Por favor selecciona un archivo ZIP')
        return
      }

      this.importing = true
      const formData = new FormData()
      formData.append('multimedia_zip', this.selectedFile)

      try {
        const response = await fetch('/multimedia/import', {
          method: 'POST',
          headers: {
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
          },
          body: formData
        })

        const result = await response.json()

        if (response.ok) {
          this.$toast.success(result.message)
          this.removeFile()
          // Recargar la página para actualizar estadísticas
          window.location.reload()
        } else {
          this.$toast.error(result.message || 'Error al importar multimedia')
        }
      } catch (error) {
        this.$toast.error('Error al importar multimedia')
        console.error('Error:', error)
      } finally {
        this.importing = false
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
  }
}
</script> 